import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { PlayerAvatar, RatingChip } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { effectiveRating } from '@/features/matches/derivedRating';
import type { PlayerMatchStatWithPlayer } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** A single stat cell: the value over a small label. Only shown when present. */
function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <View className="min-w-[88px] flex-1 items-center gap-1 rounded-card bg-surface-2/60 px-2 py-3">
      <AppText variant="heading" weight="bold" tabular className="text-ink">
        {value}
      </AppText>
      <AppText variant="micro" className="text-center text-muted" numberOfLines={2}>
        {label}
      </AppText>
    </View>
  );
}

/** A goal/assist/card highlight chip for the top contribution strip. */
function HighlightChip({
  icon,
  tint,
  label,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  tint: string;
  label: string;
}) {
  return (
    <View
      className="flex-row items-center gap-1.5 rounded-full px-3 py-1.5"
      style={{ backgroundColor: `${tint}22` }}
    >
      <Ionicons name={icon} size={13} color={tint} />
      <AppText variant="micro" weight="bold" style={{ color: tint }}>
        {label}
      </AppText>
    </View>
  );
}

export interface PlayerStatSheetProps {
  /** The tapped player's match stats; null closes the sheet. */
  stat: PlayerMatchStatWithPlayer | null;
  /** The player's team name, for the subtitle. */
  teamName?: string | null;
  onClose: () => void;
}

/**
 * A bottom-sheet for ONE player's match stat line (match-center depth, FotMob /
 * 365Scores style). Opened by tapping a player in the ratings list or a lineup
 * pitch dot. It renders the player's photo, name, position, rating, a goal /
 * assist / card highlight strip, and a grid of every available match stat. Only
 * stats present in the data are shown - nothing is invented. The per-player
 * rich stats we store are minutes, goals, assists, shots, passes, tackles,
 * saves, goals conceded, cards, penalties saved/missed, own goals and rating.
 */
export function PlayerStatSheet({ stat, teamName, onClose }: PlayerStatSheetProps) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const insets = useSafeAreaInsets();

  const er = stat ? effectiveRating(stat) : null;
  const num = (value: number) => localizeDigits(String(value), arabicNumerals);

  const name = stat?.player?.known_as || stat?.player?.name || '-';
  const position = stat?.player?.position;
  const isKeeper = position === 'GK';

  // Build the grid from the structured columns only, in a sensible reading
  // order. Each tile is gated on whether the stat is meaningful for this player
  // (e.g. saves/conceded only for keepers; penalty/own-goal lines only when > 0)
  // so the sheet never shows a wall of zeroes.
  const tiles = useMemo(() => {
    if (!stat) return [] as { label: string; value: string }[];
    const cells: { label: string; value: string }[] = [];
    const push = (label: string, value: number, when = true) => {
      if (when) cells.push({ label, value: num(value) });
    };
    push(t('match.playerStats.minutes'), stat.minutes);
    push(t('match.playerStats.goals'), stat.goals, stat.goals > 0);
    push(t('match.playerStats.assists'), stat.assists, stat.assists > 0);
    push(t('match.playerStats.shots'), stat.shots, stat.shots > 0);
    push(t('match.playerStats.passes'), stat.passes, stat.passes > 0);
    push(t('match.playerStats.tackles'), stat.tackles, stat.tackles > 0);
    push(t('match.playerStats.saves'), stat.saves, isKeeper || stat.saves > 0);
    push(t('match.playerStats.conceded'), stat.goals_conceded, isKeeper);
    push(t('match.playerStats.yellowCards'), stat.yellow, stat.yellow > 0);
    push(t('match.playerStats.redCards'), stat.red, stat.red > 0);
    push(t('match.playerStats.penaltySaved'), stat.penalty_saved, stat.penalty_saved > 0);
    push(t('match.playerStats.penaltyMissed'), stat.penalty_missed, stat.penalty_missed > 0);
    push(t('match.playerStats.ownGoals'), stat.own_goals, stat.own_goals > 0);
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stat, t, arabicNumerals, isKeeper]);

  // The headline contributions, as coloured chips above the grid.
  const highlights = useMemo(() => {
    if (!stat) return [] as React.ReactNode[];
    const chips: React.ReactNode[] = [];
    if (stat.goals > 0)
      chips.push(
        <HighlightChip
          key="g"
          icon="football"
          tint={palette.voltBright}
          label={t('match.playerStats.goalsCount', { count: stat.goals })}
        />,
      );
    if (stat.assists > 0)
      chips.push(
        <HighlightChip
          key="a"
          icon="navigate"
          tint={palette.win}
          label={t('match.playerStats.assistsCount', { count: stat.assists })}
        />,
      );
    if (stat.red > 0)
      chips.push(
        <HighlightChip
          key="r"
          icon="square"
          tint={palette.loss}
          label={t('match.playerStats.redCard')}
        />,
      );
    else if (stat.yellow > 0)
      chips.push(
        <HighlightChip
          key="y"
          icon="square"
          tint={palette.captain}
          label={t('match.playerStats.yellowCard')}
        />,
      );
    return chips;
  }, [stat, palette, t]);

  return (
    <Modal visible={stat !== null} animationType="slide" transparent onRequestClose={onClose}>
      <Pressable className="flex-1 justify-end bg-black/60" onPress={onClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="max-h-[88%] rounded-t-3xl border border-line bg-surface"
          style={{ paddingBottom: insets.bottom + 16 }}
        >
          {/* Grab handle */}
          <View className="items-center pt-3">
            <View className="h-1 w-10 rounded-full bg-line" />
          </View>

          {stat ? (
            <ScrollView contentContainerStyle={{ paddingBottom: 8 }}>
              {/* Header: photo · name/position/team · rating */}
              <View className="flex-row items-center gap-3.5 px-5 pb-4 pt-4">
                <View
                  className="overflow-hidden rounded-full"
                  style={{ borderWidth: 1.5, borderColor: palette.voltLine }}
                >
                  <PlayerAvatar uri={stat.player?.photo_url} name={name} size={56} />
                </View>
                <View className="flex-1">
                  <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={1}>
                    {name}
                  </AppText>
                  <AppText variant="micro" className="mt-0.5 text-muted" numberOfLines={1}>
                    {position ? t(`player.positions.${position}`) : ''}
                    {teamName ? `${position ? '  ·  ' : ''}${teamName}` : ''}
                  </AppText>
                </View>
                <View className="items-center gap-1">
                  <RatingChip rating={er?.value ?? null} size="lg" derived={er?.derived} />
                  <AppText variant="micro" className="text-muted">
                    {t('match.playerStats.rating')}
                  </AppText>
                </View>
              </View>

              {er?.derived ? (
                <AppText variant="micro" className="px-5 pb-2 text-muted">
                  {t('match.playerStats.derivedNote')}
                </AppText>
              ) : null}

              {/* Contribution highlights */}
              {highlights.length > 0 ? (
                <View className="flex-row flex-wrap gap-2 px-5 pb-3">{highlights}</View>
              ) : null}

              {/* Stat grid */}
              <View className="px-5 pt-1">
                <AppText
                  variant="micro"
                  weight="bold"
                  className="mb-2 uppercase tracking-widest text-soft"
                >
                  {t('match.playerStats.title')}
                </AppText>
                <View className="flex-row flex-wrap gap-2">
                  {tiles.map((tile) => (
                    <StatCell key={tile.label} label={tile.label} value={tile.value} />
                  ))}
                </View>
              </View>

              {/* Full season profile link */}
              {stat.player ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    const id = stat.player?.id;
                    onClose();
                    if (id) router.push(`/player/${id}`);
                  }}
                  className="mx-5 mt-4 flex-row items-center justify-center gap-1.5 rounded-card border border-volt-line bg-volt-glass py-3 active:opacity-80"
                >
                  <AppText variant="caption" weight="bold" className="text-volt-bright">
                    {t('match.playerStats.fullProfile')}
                  </AppText>
                  <Ionicons name="arrow-forward" size={15} color={palette.voltBright} />
                </Pressable>
              ) : null}
            </ScrollView>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
